<template>
	<div class="pl" :class="['pl--' + layout, 'pl--img-' + imageSide, 'pl--thumb-' + imageSize, { 'pl--loading': loading }]" :style="gridVars">
		<h2 v-if="archiveTitle" class="pl__title">{{ archiveTitle }}</h2>
		<p v-if="archiveDescription" class="pl__desc">{{ archiveDescription }}</p>

		<!-- section header: heading with rule and an optional "See all" link -->
		<div v-if="heading || moreLabel" class="pl__head">
			<h2 v-if="heading" class="pl__heading">{{ heading }}</h2>
			<span class="pl__rule"></span>
			<a v-if="moreLabel" class="pl__more-link" :href="moreUrl || '#'">{{ moreLabel }} <i class="fa fa-angle-right"></i></a>
		</div>

		<!-- empty -->
		<div v-if="!loading && !items.length" class="pl__empty" data-testid="cms-post-list-empty">
			<i class="fa fa-newspaper-o"></i>
			<p>{{ emptyMessage }}</p>
		</div>

		<!-- hero: lead + rest -->
		<template v-else-if="layout === 'hero'">
			<div class="pl__hero" :class="{ 'pl__hero--solo': !hero.rest.length }">
				<article v-if="hero.lead" class="pl__item pl__item--lead" :data-post-id="hero.lead.websitePostID">
					<a v-if="showImage && hero.lead.websitePostFeatureImageUrl" class="pl__img pl__img--lead" :href="hero.lead.websitePostUrl"><img class="pl__pic" v-bind="pic(hero.lead, hero.rest.length ? 'wide' : 'lead')" /><span v-if="fmt(hero.lead)" class="pl__play" :class="'pl__play--' + fmt(hero.lead)"><i class="fa" :class="fmt(hero.lead) === 'audio' ? 'fa-headphones' : 'fa-play'"></i><span v-if="duration(hero.lead)">{{ duration(hero.lead) }}</span></span><small v-if="creditFor(hero.lead)" class="pl__credit">{{ creditFor(hero.lead) }}</small></a>
					<div class="pl__body">
						<span v-if="showCategory && cat(hero.lead)" class="pl__cat" :style="catStyle">{{ cat(hero.lead) }}</span>
						<h3 class="pl__h" :style="titleStyle"><a :href="hero.lead.websitePostUrl">{{ hero.lead.websitePostTitle }}</a></h3>
						<p v-if="showExcerpt && hero.lead.websitePostExcerpt" class="pl__excerpt" :style="excerptStyle">{{ hero.lead.websitePostExcerpt }}</p>
						<div class="pl__meta" :style="metaStyle"><slot name="badge" :item="hero.lead"></slot><span v-if="by(hero.lead)" class="pl__by">by <b>{{ by(hero.lead) }}</b></span><span v-if="by(hero.lead) && when(hero.lead)" class="pl__sep">/</span><span v-if="when(hero.lead)" class="pl__date">{{ when(hero.lead) }}</span></div>
					</div>
				</article>
				<div class="pl__side">
					<article v-for="it in hero.rest" :key="it.websitePostID" class="pl__item pl__item--side" :data-post-id="it.websitePostID">
						<a v-if="showImage && it.websitePostFeatureImageUrl" class="pl__img pl__img--thumb" :href="it.websitePostUrl"><img class="pl__pic" v-bind="pic(it, 'thumb')" /></a>
						<div class="pl__body">
							<span v-if="showCategory && cat(it)" class="pl__cat" :style="catStyle">{{ cat(it) }}</span>
							<h3 class="pl__h pl__h--sm" :style="titleStyle"><a :href="it.websitePostUrl">{{ it.websitePostTitle }}</a></h3>
							<div class="pl__meta" :style="metaStyle"><slot name="badge" :item="it"></slot><span v-if="by(it)" class="pl__by">by <b>{{ by(it) }}</b></span><span v-if="by(it) && when(it)" class="pl__sep">/</span><span v-if="when(it)" class="pl__date">{{ when(it) }}</span></div>
						</div>
					</article>
				</div>
			</div>
		</template>

		<!-- playlist: big player for the selected video, episode list beside it -->
		<template v-else-if="layout === 'playlist'">
			<div class="pl__playlist">
				<div class="pl__player">
					<iframe v-if="activeEmbed && !activeIsAudio" :src="activeEmbed" class="pl__player-frame" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowfullscreen loading="lazy" :title="active.websitePostTitle"></iframe>
					<audio v-else-if="activeEmbed && activeIsAudio" :src="activeEmbed" class="pl__player-audio" controls preload="none"></audio>
					<a v-else-if="active" class="pl__img pl__img--lead" :href="active.websitePostUrl"><img class="pl__pic" v-bind="pic(active, 'wide')" /><span class="pl__play"><i class="fa fa-play"></i></span></a>
					<div v-if="active" class="pl__body pl__player-body">
						<span v-if="showCategory && cat(active)" class="pl__cat" :style="catStyle">{{ cat(active) }}</span>
						<h3 class="pl__h" :style="titleStyle"><a :href="active.websitePostUrl">{{ active.websitePostTitle }}</a></h3>
						<p v-if="showExcerpt && active.websitePostExcerpt" class="pl__excerpt" :style="excerptStyle">{{ active.websitePostExcerpt }}</p>
					</div>
				</div>
				<ol class="pl__episodes pl__episodes--side">
					<li v-for="(it, i) in items" :key="it.websitePostID" class="pl__episode" :class="{ 'pl__episode--active': i === activeIndex }" :data-post-id="it.websitePostID">
						<button type="button" class="pl__episode-btn" @click="activeIndex = i">
							<span class="pl__episode-thumb"><img v-if="it.websitePostFeatureImageUrl" class="pl__pic" v-bind="pic(it, 'thumb')" /><i class="fa" :class="i === activeIndex ? 'fa-volume-up' : 'fa-play'"></i></span>
							<span class="pl__body">
								<span class="pl__h pl__h--sm" :style="titleStyle">{{ it.websitePostTitle }}</span>
								<span class="pl__meta" :style="metaStyle"><span v-if="duration(it)" class="pl__duration">{{ duration(it) }}</span><span v-if="duration(it) && when(it)" class="pl__sep">/</span><span v-if="when(it)" class="pl__date">{{ when(it) }}</span></span>
							</span>
						</button>
					</li>
				</ol>
			</div>
		</template>

		<!-- episodes: compact rows with an inline player for the chosen one -->
		<template v-else-if="layout === 'episodes'">
			<ol class="pl__episodes">
				<li v-for="(it, i) in items" :key="it.websitePostID" class="pl__episode" :class="{ 'pl__episode--active': i === activeIndex }" :data-post-id="it.websitePostID">
					<div class="pl__episode-row">
						<button type="button" class="pl__episode-play" :aria-label="(i === activeIndex ? 'Hide ' : 'Play ') + it.websitePostTitle" @click="activeIndex = i === activeIndex ? -1 : i"><i class="fa" :class="i === activeIndex ? 'fa-stop' : 'fa-play'"></i></button>
						<a v-if="showImage && it.websitePostFeatureImageUrl" class="pl__img pl__img--thumb" :href="it.websitePostUrl"><img class="pl__pic" v-bind="pic(it, 'thumb')" /></a>
						<div class="pl__body">
							<span v-if="showCategory && cat(it)" class="pl__cat" :style="catStyle">{{ cat(it) }}</span>
							<h3 class="pl__h pl__h--sm" :style="titleStyle"><a :href="it.websitePostUrl">{{ it.websitePostTitle }}</a></h3>
							<p v-if="showExcerpt && it.websitePostExcerpt" class="pl__excerpt pl__excerpt--one" :style="excerptStyle">{{ it.websitePostExcerpt }}</p>
							<div class="pl__meta" :style="metaStyle"><span v-if="duration(it)" class="pl__duration">{{ duration(it) }}</span><span v-if="duration(it) && when(it)" class="pl__sep">/</span><span v-if="when(it)" class="pl__date">{{ when(it) }}</span></div>
						</div>
					</div>
					<div v-if="i === activeIndex && embedFor(it)" class="pl__episode-player">
						<audio v-if="isAudio(it)" :src="embedFor(it)" controls autoplay preload="none" class="pl__player-audio"></audio>
						<iframe v-else :src="embedFor(it)" class="pl__player-frame" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowfullscreen :title="it.websitePostTitle"></iframe>
					</div>
				</li>
			</ol>
		</template>

		<!-- grid / cards / list -->
		<div v-else class="pl__items">
			<article v-for="(it, i) in items" :key="it.websitePostID" class="pl__item" :data-post-id="it.websitePostID">
				<a v-if="showImage && it.websitePostFeatureImageUrl" class="pl__img" :href="it.websitePostUrl"><img class="pl__pic" v-bind="pic(it, layout === 'cover' && i === 0 ? 'lead' : '')" /><span v-if="fmt(it)" class="pl__play" :class="'pl__play--' + fmt(it)"><i class="fa" :class="fmt(it) === 'audio' ? 'fa-headphones' : 'fa-play'"></i><span v-if="duration(it)">{{ duration(it) }}</span></span></a>
				<div class="pl__body">
					<span v-if="showCategory && cat(it)" class="pl__cat" :style="catStyle">{{ cat(it) }}</span>
					<h3 class="pl__h" :style="titleStyle"><a :href="it.websitePostUrl">{{ it.websitePostTitle }}</a></h3>
					<p v-if="showExcerpt && it.websitePostExcerpt" class="pl__excerpt" :style="excerptStyle">{{ it.websitePostExcerpt }}</p>
					<div class="pl__meta" :style="metaStyle"><slot name="badge" :item="it"></slot><span v-if="by(it)" class="pl__by">by <b>{{ by(it) }}</b></span><span v-if="by(it) && when(it)" class="pl__sep">/</span><span v-if="when(it)" class="pl__date">{{ when(it) }}</span></div>
				</div>
			</article>
		</div>

		<!-- pagination -->
		<div v-if="pagination === 'loadMore' && hasMore" class="pl__more">
			<button type="button" class="pl__more-btn" :disabled="loadingMore" @click="loadMore">
				<i v-if="loadingMore" class="fa fa-circle-o-notch fa-spin"></i> {{ loadingMore ? t('common.loading') : t('common.loadMore') }}
			</button>
		</div>
		<nav v-else-if="pagination === 'pages' && pagesTotal > 1" class="pl__pages" aria-label="Pages">
			<a v-if="pageNumber > 1" class="pl__page" :href="pageUrl(pageNumber - 1)">&laquo; Newer</a>
			<span class="pl__page pl__page--info">Page {{ pageNumber }} of {{ pagesTotal }}</span>
			<a v-if="pageNumber < pagesTotal" class="pl__page" :href="pageUrl(pageNumber + 1)">Older &raquo;</a>
		</nav>
	</div>
</template>

<script>
/**
 * PostListBlock.vue — public variant
 * ──────────────────────────────────
 * ONE block for every post listing: latest, one category, a tag, or
 * hand-picked posts; hero / grid / list / cards / cover layouts.
 *
 * Data comes from one of two places:
 *   1. Server injection — on Category / Tag / Author / Search templates the
 *      TemplateResolverService fills `posts`, `currentPage`, `totalPages`,
 *      `basePath`, `archiveTitle` on the first Post List whose source mode
 *      is `context`. No client fetch happens.
 *   2. Client fetch — otherwise the block loads from /ajax/getWebsitePosts.php
 *      using `source`.
 *
 * Styling: outer spacing/background/typography come from the wrapper traits.
 * Card chrome uses the site tokens (--site-*), so one Site Design change
 * restyles every list.
 */
import { formatPostDate, buildTypographyStyle } from '@/websites/cms/blocks/shared/blockUtils.js';
import { t } from '@/websites/i18n/websiteI18n';
import { creditLine } from '../shared/imageCredit.js';
import { imgAttrs } from '@/websites/cms/blocks/shared/imageSrc.js';
import { pictureCandidates } from '@/websites/cms/blocks/shared/postContext.js';
import { toEmbedUrl, isAudioFile, formatDuration, postFormat } from '@/websites/cms/blocks/shared/mediaUtils.js';
import { PUBLIC_POSTS_URL, normalizeSource, buildPostsQuery, firstLabel, splitHero } from './postListShared.js';
import registry, { applyExclusions, OVERFETCH, MAX_FETCH } from './postListRegistry.js';

export default {
	name: 'PostListBlock',

	props: {
		heading: { type: String, default: '' },
		moreLabel: { type: String, default: '' },
		moreUrl: { type: String, default: '' },
		source: { type: [Object, String], default: null },
		layout: { type: String, default: 'grid' },
		count: { type: [Number, String], default: 6 },
		columns: { type: [Number, String], default: 3 },
		imageSide: { type: String, default: 'left' },
		imageSize: { type: String, default: 'medium' },
		cardPadding: { type: String, default: '1rem' },
		// per-element text styles (typography config fields)
		titleTypography: { type: Object, default: null },
		excerptTypography: { type: Object, default: null },
		metaTypography: { type: Object, default: null },
		categoryTypography: { type: Object, default: null },
		showImage: { type: [Boolean, String], default: true },
		showExcerpt: { type: [Boolean, String], default: true },
		showDate: { type: [Boolean, String], default: true },
		showAuthor: { type: [Boolean, String], default: false },
		showCategory: { type: [Boolean, String], default: true },
		// repeat protection: page (default) | custom (uniqueGroupName) | none
		uniqueGroup: { type: String, default: 'page' },
		uniqueGroupName: { type: String, default: '' },
		pagination: { type: String, default: 'none' },
		emptyText: { type: String, default: 'Publish your first post to see it here.' },

		// ── server-injected on archive templates ──
		posts: { type: Array, default: null },
		currentPage: { type: [Number, String], default: 1 },
		totalPages: { type: [Number, String], default: 1 },
		totalPosts: { type: [Number, String], default: 0 },
		basePath: { type: String, default: '' },
		archiveTitle: { type: String, default: '' },
		archiveDescription: { type: String, default: '' }
	},

	// Previews inside the editor provide the Application endpoint and website
	// id; the public site leaves both unset and uses the site's own endpoint.
	inject: {
		cmsPostsEndpoint: { default: null },
		cmsPageContext: { default: null },
		siteSettings: { default: function () { return {}; } }
	},

	data: function () {
		return {
			/** Rendered width of the block (px), for the srcset `sizes` hint */
			rootWidth: 0,
			widthObserver: null,
			fetched: [],
			fetchedTotal: 0,
			page: 1,
			loading: false,
			loadingMore: false,
			activeIndex: 0,
			regToken: null,
			// ids the blocks ahead of this one already show; captured per fetch so paging stays stable
			excludedIds: [],
			droppedShown: 0,
			fetchSeq: 0
		};
	},

	computed: {
		src: function () { return normalizeSource(this.source); },

		/** Server-injected posts win; otherwise what we fetched. */
		injected: function () { return Array.isArray(this.posts); },

		items: function () {
			var list = this.injected ? this.posts : this.fetched;
			return this.filterItems(list);
		},

		hero: function () { return splitHero(this.items); },

		/** Empty because every match already appears above (repeat protection) reads differently from "no posts yet". */
		emptyMessage: function () {
			if (this.droppedShown > 0 && this.fetchedTotal === 0) return 'Every story that matches already appears higher on this page. Set Content group to Off to show them here too.';
			return this.emptyText;
		},

		limit: function () { return Math.max(1, parseInt(this.count, 10) || 6); },

		gridVars: function () {
			return {
				'--pl-cols': String(Math.max(1, parseInt(this.columns, 10) || 3)),
				'--pl-card-pad': this.cardPadding || '1rem'
			};
		},

		active: function () { return this.items[this.activeIndex] || null; },
		activeEmbed: function () { return this.active ? this.embedFor(this.active) : ''; },
		activeIsAudio: function () { return !!this.active && this.isAudio(this.active); },

		titleStyle: function () { return buildTypographyStyle(this.titleTypography); },
		excerptStyle: function () { return buildTypographyStyle(this.excerptTypography); },
		metaStyle: function () { return buildTypographyStyle(this.metaTypography); },
		catStyle: function () { return buildTypographyStyle(this.categoryTypography); },

		pageNumber: function () { return parseInt(this.currentPage, 10) || 1; },
		pagesTotal: function () { return parseInt(this.totalPages, 10) || 1; },

		hasMore: function () {
			if (this.injected) return false;
			return this.fetched.length < this.fetchedTotal;
		}
	},

	watch: {
		source: { deep: true, handler: function () { this.reload(); } },
		count: function () { this.reload(); },
		uniqueGroup: function () { this.joinRegistry(); this.reload(); this.claimShown(); },
		uniqueGroupName: function () { this.joinRegistry(); this.reload(); this.claimShown(); },
		// client-side navigation to another article re-targets a Related list
		'cmsPageContext.websitePostID': function () { if (this.src.mode === 'related') this.reload(); }
	},

	mounted: function () {
		this.joinRegistry();
		this.watchWidth();
		if (!this.injected && this.src.mode !== 'context') this.reload();
		else this.claimShown();
	},

	beforeDestroy: function () {
		if (this.regToken) registry.leave(this.regToken);
		if (this.widthObserver) this.widthObserver.disconnect();
		else if (typeof window !== 'undefined') window.removeEventListener('resize', this.measureWidth);
	},

	methods: {
		t,
		creditFor: function (post) { return creditLine(post && post.featureImageCredit, this.siteSettings); },

		/** Hook for the admin variant (drafts). Public shows what the server returns. */
		filterItems: function (list) { return list || []; },

		// ── Repeat protection ──
		dedupeOn: function () { return this.uniqueGroup !== 'none'; },
		dedupeGroup: function () {
			return this.uniqueGroup === 'custom' ? ('g:' + String(this.uniqueGroupName || '').trim().toLowerCase() || 'page') : 'page';
		},
		dedupeScope: function () {
			var ctx = this.cmsPageContext || {};
			return 'page:' + (ctx.websitePageID || ctx.websitePostID || 'current');
		},
		joinRegistry: function () {
			var self = this;
			if (this.regToken) { registry.leave(this.regToken); this.regToken = null; }
			if (!this.dedupeOn()) return;
			this.regToken = registry.join(this.dedupeScope(), this.dedupeGroup());
			registry.onChangeAhead(this.regToken, function () {
				// a block above now shows different stories: fetch again with the new exclusions
				if (!self.injected && self.src.mode !== 'context') self.reload();
			});
		},
		/** Tells the registry which stories this block shows, so the blocks behind skip them. */
		claimShown: function () {
			if (!this.regToken) return;
			registry.claim(this.regToken, this.items.map(function (it) { return it.websitePostID; }).filter(Boolean));
		},

		/** Endpoint + extra params. The admin variant overrides this. */
		fetchConfig: function () {
			var ep = this.cmsPostsEndpoint;
			var extra = (ep && ep.url) ? Object.assign({}, ep.extra || {}) : {};
			// Related: follow the article being viewed; without one the list falls back to latest
			if (this.src.mode === 'related' && this.cmsPageContext && this.cmsPageContext.websitePostID) {
				extra.relatedToPostID = this.cmsPageContext.websitePostID;
			}
			return { url: (ep && ep.url) ? ep.url : PUBLIC_POSTS_URL, extra: extra };
		},

		reload: function () {
			var self = this;
			if (this.injected || this.src.mode === 'context') return;
			var cfg = this.fetchConfig();
			var seq = ++this.fetchSeq;
			this.loading = true;
			this.page = 1;
			// Fetch at once (over-fetched) and, in parallel, wait for the blocks above to claim.
			// Dropping their stories happens client-side; only a dry buffer costs a second request.
			var ask = this.regToken ? Math.min(MAX_FETCH, this.limit + OVERFETCH) : this.limit;
			var first = fetch(cfg.url + '?' + buildPostsQuery(this.src, ask, 1, cfg.extra), { credentials: 'same-origin' }).then(function (r) { return r.json(); });
			var turn = this.regToken ? registry.waitForTurn(this.regToken) : Promise.resolve([]);
			Promise.all([first, turn]).then(function (res) {
				if (seq !== self.fetchSeq) return null;
				var d = res[0];
				self.excludedIds = res[1] || [];
				var items = (d && d.success && Array.isArray(d.items)) ? d.items : [];
				var total = (d && d.total) ? parseInt(d.total, 10) : items.length;
				var cut = applyExclusions(items, self.excludedIds, self.limit);
				self.droppedShown = cut.dropped;
				if (!cut.short) return { success: true, items: cut.items, total: Math.max(cut.items.length, total - cut.dropped) };
				var extra = Object.assign({}, cfg.extra, { excludePostIDs: self.excludedIds.join(',') });
				return fetch(cfg.url + '?' + buildPostsQuery(self.src, self.limit, 1, extra), { credentials: 'same-origin' }).then(function (r) { return r.json(); });
			})
				.then(function (d) {
					if (seq !== self.fetchSeq) return;
					if (d !== null) {
						self.fetched = (d && d.success && Array.isArray(d.items)) ? d.items : [];
						self.fetchedTotal = (d && d.total) ? parseInt(d.total, 10) : self.fetched.length;
					}
				})
				.catch(function () { if (seq === self.fetchSeq) { self.fetched = []; self.fetchedTotal = 0; } })
				.then(function () {
					if (seq !== self.fetchSeq) return;
					self.loading = false;
					// claim even when empty, so the blocks behind are not held up
					self.claimShown();
				});
		},

		loadMore: function () {
			var self = this;
			if (this.loadingMore || !this.hasMore) return;
			var cfg = this.fetchConfig();
			var next = this.page + 1;
			this.loadingMore = true;
			var extra = Object.assign({}, cfg.extra, this.excludedIds.length ? { excludePostIDs: this.excludedIds.join(',') } : {});
			fetch(cfg.url + '?' + buildPostsQuery(this.src, this.limit, next, extra), { credentials: 'same-origin' })
				.then(function (r) { return r.json(); })
				.then(function (d) {
					if (d && d.success && Array.isArray(d.items) && d.items.length) {
						self.fetched = self.fetched.concat(d.items);
						self.page = next;
						self.fetchedTotal = parseInt(d.total, 10) || self.fetchedTotal;
					} else {
						self.fetchedTotal = self.fetched.length;
					}
				})
				.catch(function () { self.fetchedTotal = self.fetched.length; })
				.then(function () { self.loadingMore = false; });
		},

		/**
		 * <img> attributes with a srcset, so the browser picks the variant for
		 * the rendered width and the screen density: a lead that spans the
		 * page gets up to 2560px on a Retina screen, a thumb stays small.
		 * Every width is a step of the server ladder (imageSrc.js LADDER).
		 * `size`: 'lead' (cover's full-row first tile), 'wide' (hero lead and
		 * playlist player: two thirds of the row), 'thumb' (side lists,
		 * episodes) or '' (grid / card / list tile).
		 */
		pic: function (it, size) {
			var base = it.websitePostFeatureImageUrl || '';
			var large = it.websitePostFeatureImageUrlLarge || '';
			var full = it.websitePostFeatureImageUrlFull || '';
			// Wide tiles start from the biggest copy; WordPress-synced posts keep only a ~300px copy in the base column.
			var url = (size === 'lead' || size === 'wide') ? (full || large || base) : (size === 'thumb' ? base : (large || base));
			var pageW = parseInt((this.siteSettings && this.siteSettings.pageWidth) || '', 10) || 1280;
			var cols = Math.max(1, parseInt(this.columns, 10) || 3);
			// The block's real width once measured (it may sit in a column); the page width until then.
			var row = this.rootWidth > 0 ? this.rootWidth + 'px' : 'min(100vw, ' + pageW + 'px)';
			var spec;
			if (size === 'lead') spec = { widths: [640, 960, 1280, 1920, 2560], fallbackWidth: 1920, sizes: row, priority: true };
			else if (size === 'wide') spec = { widths: [640, 960, 1280, 1920], fallbackWidth: 1280, sizes: '(max-width: 900px) ' + row + ', calc(' + row + ' * 0.66)', priority: true };
			else if (size === 'thumb') spec = { widths: [320, 640], fallbackWidth: 320, sizes: '160px' };
			else if (this.layout === 'list') spec = { widths: [320, 640, 960], fallbackWidth: 640, sizes: '(max-width: 768px) 45vw, 320px' };
			else spec = { widths: [320, 640, 960, 1280], fallbackWidth: 640, sizes: '(max-width: 768px) ' + row + ', calc(' + row + ' / ' + cols + ')' };
			// copies of a remote (WordPress) picture, labelled by the width in their file name
			spec.candidates = pictureCandidates(it);
			var attrs = imgAttrs(url, spec);
			attrs.alt = it.websitePostTitle || '';
			return attrs;
		},

		/** Keeps rootWidth equal to the block's rendered width so `sizes` matches a block placed in a column. */
		watchWidth: function () {
			var self = this;
			this.measureWidth();
			if (typeof ResizeObserver !== 'undefined' && this.$el && this.$el.nodeType === 1) {
				this.widthObserver = new ResizeObserver(function () { self.measureWidth(); });
				this.widthObserver.observe(this.$el);
			} else if (typeof window !== 'undefined') {
				window.addEventListener('resize', this.measureWidth);
			}
		},
		measureWidth: function () {
			var w = this.$el && this.$el.nodeType === 1 ? Math.round(this.$el.clientWidth) : 0;
			// only widen-by-measure matters for sharpness; ignore jitter under 24px
			if (w > 0 && Math.abs(w - this.rootWidth) >= 24) this.rootWidth = w;
		},

		cat: function (it) { return firstLabel(it.categories); },
		fmt: postFormat,
		duration: function (it) { return formatDuration(it && it.mediaDurationSeconds); },
		embedFor: function (it) { return toEmbedUrl(it && it.mediaUrl); },
		isAudio: function (it) { return postFormat(it) === 'audio' || isAudioFile(it && it.mediaUrl); },

		/** Author label when Show author is on. */
		by: function (it) {
			return this.isOn(this.showAuthor) ? firstLabel(it.authors) : '';
		},

		/** Formatted date when Show date is on. */
		when: function (it) {
			return (this.isOn(this.showDate) && it.websitePostDate) ? formatPostDate(it.websitePostDate) : '';
		},

		/** Plain-text meta line (kept for tests and the admin variant). */
		meta: function (it) {
			var parts = [];
			if (this.when(it)) parts.push(this.when(it));
			if (this.by(it)) parts.push(this.by(it));
			return parts.join(' · ');
		},

		isOn: function (v) { return v === true || v === 'true' || v === 1 || v === '1'; },

		pageUrl: function (n) {
			var base = this.basePath || (typeof window !== 'undefined' ? window.location.pathname : '/');
			return n <= 1 ? base : base.replace(/\/$/, '') + '/page/' + n;
		}
	}
};
</script>

<style scoped>
.pl { width: 100%; max-width: 100%; color: inherit; }
.pl__title { margin: 0 0 .25em; font-family: var(--site-h2-font, var(--site-heading-font)); font-size: var(--site-h2-size); font-weight: var(--site-h2-weight); color: var(--site-h2-color, var(--site-heading-color)); }
.pl__desc { margin: 0 0 1.5rem; opacity: .8; }

/* section header */
.pl__head { display: flex; align-items: center; gap: 1rem; margin: 0 0 1.25rem; }
.pl__heading { margin: 0; font-family: var(--site-h2-font, var(--site-heading-font)); font-size: var(--site-h2-size, 2em); font-weight: var(--site-h2-weight, 700); line-height: 1.1; color: var(--site-h2-color, var(--site-heading-color)); white-space: nowrap; }
.pl__rule { flex: 1; height: 2px; background: var(--site-heading-color, #1E2031); }
.pl__more-link { font-family: var(--site-body-font, inherit); font-size: .75em; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--site-heading-color, inherit); text-decoration: none; white-space: nowrap; }
.pl__more-link:hover { color: var(--site-accent-color, #FB6A00); }

/* items */
.pl { container-type: inline-size; }
.pl__items { display: grid; gap: 2rem 1.5rem; grid-template-columns: repeat(var(--pl-cols, 3), minmax(0, 1fr)); }
.pl--list .pl__items { grid-template-columns: 1fr; gap: 1.5rem; }
.pl__item { display: flex; flex-direction: column; gap: .75rem; min-width: 0; }
.pl--list .pl__item { flex-direction: row; gap: 1.25rem; align-items: flex-start; padding-bottom: 1.5rem; border-bottom: 1px solid var(--site-hr-color, #DDDEE0); }
.pl--list .pl__item:last-child { border-bottom: 0; padding-bottom: 0; }
.pl--cards .pl__item { background: var(--site-page-color, #FFFFFF); border: 1px solid var(--site-hr-color, #DDDEE0); border-radius: 10px; overflow: hidden; gap: 0; }
.pl--cards .pl__body { padding: var(--pl-card-pad, 1rem); }
.pl--cards .pl__img { border-radius: 0; }
/* image above the text in list and hero side rows */
.pl--img-top.pl--list .pl__item, .pl--img-top .pl__item--side { flex-direction: column; }
.pl--img-top.pl--list .pl__img, .pl--img-top .pl__img--thumb { flex-basis: auto; width: 100%; height: auto; aspect-ratio: 16 / 10; }

.pl__img { display: block; position: relative; overflow: hidden; aspect-ratio: 16 / 10; background: #E9E9EA; border-radius: var(--site-image-radius, 6px); }
.pl__pic { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
.pl--list .pl__img { flex: 0 0 var(--pl-thumb, 200px); width: var(--pl-thumb, 200px); min-width: 0; max-width: 45%; aspect-ratio: 4 / 3; }
.pl--thumb-small { --pl-thumb: 110px; }
.pl--thumb-medium { --pl-thumb: 200px; }
.pl--thumb-large { --pl-thumb: 280px; }
.pl--img-right.pl--list .pl__item, .pl--img-right .pl__item--side { flex-direction: row-reverse; }
.pl__body { display: flex; flex-direction: column; gap: .35rem; min-width: 0; flex: 1; }
.pl__cat { font-size: .75em; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--site-accent-color, #FB6A00); }
.pl__h { margin: 0; font-family: var(--site-h3-font, var(--site-heading-font)); font-size: 1.25em; font-weight: var(--site-h3-weight, 600); line-height: 1.25; color: var(--site-heading-color, inherit); }
.pl__h a { color: inherit; text-decoration: none; }
.pl__h a:hover { color: var(--site-accent-color, #FB6A00); }
.pl__h--sm { font-size: 1.05em; }
.pl__excerpt { margin: 0; opacity: .85; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.pl__meta { font-family: var(--site-body-font, inherit); font-size: .78em; opacity: .75; display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
.pl__by { font-style: italic; }
.pl__by b { font-style: normal; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.pl__sep { opacity: .5; }

/* hero */
.pl__hero { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 2rem; }
/* one story only: the lead takes the full width instead of leaving an empty side column */
.pl__hero--solo { grid-template-columns: 1fr; }
.pl__hero--solo .pl__side { display: none; }
.pl__item--lead .pl__h { font-size: var(--site-h2-size, 2em); font-family: var(--site-h2-font, var(--site-heading-font)); font-weight: var(--site-h2-weight, 700); line-height: 1.15; }
.pl__img--lead { aspect-ratio: 16 / 9; }
.pl__credit { position: absolute; right: .5rem; bottom: .4rem; padding: .15rem .45rem; border-radius: 3px; background: rgba(0,0,0,.45); color: #FFF; font-size: .68em; letter-spacing: .02em; line-height: 1.3; }
.pl__side { display: flex; flex-direction: column; gap: 1.5rem; }
.pl__item--side { padding-bottom: 1.5rem; border-bottom: 1px solid var(--site-hr-color, #DDDEE0); }
.pl__item--side:last-child { padding-bottom: 0; border-bottom: 0; }
.pl__item--side { flex-direction: row; gap: .9rem; align-items: flex-start; }
/* fixed box: a stretched height would otherwise widen the square thumb and shift the text column */
.pl__img--thumb { flex: 0 0 110px; width: 110px; height: 110px; aspect-ratio: auto; align-self: flex-start; }
.pl--list .pl__img { align-self: flex-start; }

/* cover: photo tiles with the headline over a gradient; first item spans the row */
.pl--cover .pl__items { gap: 1rem; }
.pl--cover .pl__item { position: relative; aspect-ratio: 4 / 3; border-radius: var(--site-image-radius, 6px); overflow: hidden; background: #1E2031; color: #FFFFFF; gap: 0; }
.pl--cover .pl__item:first-child { grid-column: 1 / -1; aspect-ratio: 21 / 9; }
.pl--cover .pl__img { position: absolute; inset: 0; aspect-ratio: auto; border-radius: 0; transition: transform .6s ease; }
.pl--cover .pl__item:hover .pl__img { transform: scale(1.03); }
.pl--cover .pl__body { position: absolute; left: 0; right: 0; bottom: 0; padding: 1.5rem; background: linear-gradient(to top, rgba(0,0,0,.85) 0%, rgba(0,0,0,.45) 60%, rgba(0,0,0,0) 100%); color: #FFFFFF; }
.pl--cover .pl__item:first-child .pl__body { padding: 2.5rem; max-width: 760px; }
.pl--cover .pl__h { color: #FFFFFF; font-size: 1.35em; text-shadow: 0 1px 2px rgba(0,0,0,.4); }
.pl--cover .pl__item:first-child .pl__h { font-size: var(--site-h1-size, 2.75em); font-family: var(--site-h1-font, var(--site-heading-font)); font-weight: var(--site-h1-weight, 700); line-height: 1.05; letter-spacing: var(--site-h1-letter-spacing, -0.02em); }
.pl--cover .pl__h a:hover { color: #FFFFFF; text-decoration: underline; }
.pl--cover .pl__excerpt { display: none; opacity: .9; }
.pl--cover .pl__item:first-child .pl__excerpt { display: -webkit-box; -webkit-line-clamp: 2; }
.pl--cover .pl__cat { color: #FFFFFF; background: var(--site-accent-color, #FB6A00); align-self: flex-start; padding: .2em .6em; border-radius: 2px; }
.pl--cover .pl__meta { opacity: .85; }

/* play badge on tiles */
.pl__img { position: relative; }
.pl__play { position: absolute; left: .6rem; bottom: .6rem; display: inline-flex; align-items: center; gap: .4rem; padding: .3rem .6rem; border-radius: 999px; background: rgba(0,0,0,.7); color: #FFF; font-size: .75em; font-weight: 700; letter-spacing: .02em; }
.pl__play .fa { font-size: .9em; }
.pl__img--lead .pl__play { left: 1rem; bottom: 1rem; padding: .5rem .9rem; font-size: .85em; }
.pl__duration { font-variant-numeric: tabular-nums; }

/* playlist */
.pl__playlist { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 1.5rem; align-items: start; }
.pl__player { display: flex; flex-direction: column; gap: .75rem; }
.pl__player-frame { width: 100%; aspect-ratio: 16 / 9; border: 0; border-radius: var(--site-image-radius, 6px); background: #000; }
.pl__player-audio { width: 100%; }
.pl__player .pl__img--lead { display: flex; align-items: center; justify-content: center; }
.pl__player .pl__img--lead .pl__play { position: static; width: 64px; height: 64px; justify-content: center; border-radius: 50%; padding: 0; font-size: 1.4em; }
.pl__episodes { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
.pl__episodes--side { max-height: 520px; overflow-y: auto; border: 1px solid var(--site-hr-color, #DDDEE0); border-radius: var(--site-image-radius, 6px); }
.pl__episode { border-bottom: 1px solid var(--site-hr-color, #DDDEE0); }
.pl__episode:last-child { border-bottom: 0; }
.pl__episode-btn { display: flex; gap: .75rem; width: 100%; padding: .75rem; border: 0; background: transparent; text-align: left; cursor: pointer; font: inherit; color: inherit; }
.pl__episode--active .pl__episode-btn, .pl__episode-btn:hover { background: rgba(0,0,0,.04); }
.pl__episode-thumb { position: relative; overflow: hidden; flex: 0 0 96px; width: 96px; aspect-ratio: 16 / 10; border-radius: 4px; background: #E9E9EA; display: flex; align-items: center; justify-content: center; color: #FFF; text-shadow: 0 1px 3px rgba(0,0,0,.6); }
.pl__episode-thumb > .fa { position: relative; z-index: 1; }
.pl__episodes .pl__h--sm { font-size: 1em; }
.pl__episode-row { display: flex; align-items: center; gap: .9rem; padding: .9rem 0; }
.pl__episode-play { flex: 0 0 44px; width: 44px; height: 44px; border-radius: 50%; border: 0; background: var(--site-accent-color, #FB6A00); color: #FFF; cursor: pointer; font-size: 1em; display: inline-flex; align-items: center; justify-content: center; }
.pl__episode-row .pl__img--thumb { flex-basis: 80px; width: 80px; height: 80px; }
.pl__excerpt--one { -webkit-line-clamp: 1; }
.pl__episode-player { padding: 0 0 1rem 3.65rem; }

/* empty */
.pl__empty { text-align: center; padding: 3rem 1rem; border: 1px dashed var(--site-hr-color, #DDDEE0); border-radius: 10px; opacity: .75; }
.pl__empty .fa { font-size: 2em; margin-bottom: .5rem; color: var(--site-accent-color, #FB6A00); }
.pl__empty p { margin: 0; }

/* pagination */
.pl__more { text-align: center; margin-top: 1.5rem; }
.pl__more-btn { display: inline-block; padding: var(--site-btn-padding, .625rem 1.5rem); border-radius: var(--site-btn-radius, 4px); border: 1px solid var(--site-btn-secondary-border, #DDDEE0); background: var(--site-btn-secondary-bg, #FFF); color: var(--site-btn-secondary-color, #1E2031); font-weight: var(--site-btn-font-weight, 600); cursor: pointer; }
.pl__pages { display: flex; justify-content: center; gap: 1rem; margin-top: 1.5rem; }
.pl__page { color: var(--site-link-color, #0080FF); text-decoration: none; font-weight: 600; }
.pl__page--info { opacity: .7; font-weight: 400; }

/* Narrow placement (sidebar, phone): one column and image above the text,
   whatever the viewport. Mirrors the media query below. */
@container (max-width: 420px) {
	.pl__items { grid-template-columns: 1fr !important; }
	.pl__hero { grid-template-columns: 1fr; }
	.pl--list .pl__item, .pl__item--side { flex-direction: column; }
	.pl--list .pl__img, .pl__img--thumb { flex-basis: auto; width: 100%; height: auto; aspect-ratio: 16 / 10; }
	.pl--cover .pl__item, .pl--cover .pl__item:first-child { aspect-ratio: 4 / 3; }
	.pl--cover .pl__item:first-child .pl__body { padding: 1.5rem; }
	.pl--cover .pl__item:first-child .pl__h { font-size: 1.75em; }
	.pl__playlist { grid-template-columns: 1fr; }
	.pl__episode-player { padding-left: 0; }
}

@media (max-width: 768px) {
	.pl__items { grid-template-columns: 1fr !important; }
	.pl__hero { grid-template-columns: 1fr; }
	.pl--list .pl__item, .pl__item--side { flex-direction: column; }
	.pl--list .pl__img, .pl__img--thumb { flex-basis: auto; width: 100%; height: auto; aspect-ratio: 16 / 10; }
	.pl--cover .pl__item, .pl--cover .pl__item:first-child { aspect-ratio: 4 / 3; }
	.pl--cover .pl__item:first-child .pl__body { padding: 1.5rem; }
	.pl--cover .pl__item:first-child .pl__h { font-size: 1.75em; }
	.pl__playlist { grid-template-columns: 1fr; }
	.pl__episode-player { padding-left: 0; }
}
</style>
